import { getFetcher, postFetcher } from '@/api/fetchers';
import { dateFormat, dateFormatUI } from '@/index';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { type Tags } from '@/schemas/API/GET/tag/response';
import {
  type TaskRequest,
  defaultTaskRequestSchema,
} from '@/schemas/API/POST/client-plan/task/request';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { displayError } from '@/utils/error/errorHandler';
import { badNotification } from '@/components/Notification/Notification';
import {
  Text,
  Stack,
  rem,
  TextInput,
  Group,
  Select,
  MultiSelect,
  Textarea,
  Button,
  Divider,
  NumberInput,
  CheckIcon,
  Checkbox,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconX, IconHelpCircle } from '@tabler/icons-react';
import { type Dayjs } from 'dayjs';
import { type ChangeEvent, useEffect, useState } from 'react';
import { BiSolidDownArrow } from 'react-icons/bi';
import useSWR from 'swr';
import TimeInput from '@/components/Input/TimeInput/TimeInput';

interface NewTaskModalProps {
  clientPlanId: number;
  month: Dayjs;
}

// TODO add validation for all fields, especially the ones containing times

export const NewTaskModal: React.FC<NewTaskModalProps> = props => {
  const [form, setForm] = useState<TaskRequest>({
    ...defaultTaskRequestSchema,
    date: props.month.startOf('month').format(dateFormat), // Set default date to the first day of the month
  });
  const [noteValue, setNoteValue] = useState(form.note ?? '');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateFields = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name) newErrors.name = 'Pole je povinné';
    if (form.byMonthDay.length === 0) newErrors.date = 'Pole je povinné';
    if (!form.boundStartTime) newErrors.boundStartTime = 'Pole je povinné';
    if (!form.boundEndTime) newErrors.boundEndTime = 'Pole je povinné';
    else if (form.boundEndTime <= form.boundStartTime)
      newErrors.boundEndTime = 'Konec musí být po začátku';
    if (!form.duration) newErrors.duration = 'Pole je povinné';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    setNoteValue(form.note ?? '');
  }, [form.note]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    setNoteValue(value);
    updateFormField('note', value === '' ? null : value);
  };

  const updateFormField = <K extends keyof TaskRequest>(
    fieldName: K,
    value: TaskRequest[K] | null
  ): void => {
    setForm(prev => ({ ...prev, [fieldName]: value }));
  };

  const icon = (
    <BiSolidDownArrow
      style={{ width: rem(10), height: rem(10), color: 'gray' }}
    />
  );

  const { data: genderMap } = useSWR<Mapping>('/mappings/sexes', getFetcher);
  const { data: allTags } = useSWR<Tags>(`/tags`, getFetcher);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [checked, setChecked] = useState(false);

  const tagOptions = allTags?.content.map(item => {
    return item.name;
  });

  function handleChangeTags(values: string[]): void {
    setSelectedValues(values);
    form.tags = [];
    allTags?.content.forEach(tag => {
      if (values.includes(tag.name)) form.tags.push(tag.id);
    });
  }

  const getDates = () => {
    const today = props.month.toDate();
    return form.byMonthDay.map(
      day => new Date(today.getFullYear(), today.getMonth(), day)
    );
  };

  // If post is not successful, the task may collide with existing ones...
  const handleSubmitButton = async () => {
    if (!validateFields()) {
      badNotification('Chybějící údaje', 'Prosím vyplňte všechna povinná pole');
      return;
    }

    const suffix = checked ? 'task-groups' : 'tasks';
    const url = `/client-plans/${props.clientPlanId}/` + suffix;
    try {
      if (checked) {
        await postFetcher(url, form);
      } else {
        const monthStart = props.month.startOf('month');
        const list = form.byMonthDay;
        form.byMonthDay = [];
        for (const day of list) {
          const taskDate = monthStart.date(day).format(dateFormat);
          const taskRequest = {
            ...form,
            date: taskDate,
          };
          await postFetcher(url, taskRequest);
        }
      }

      notifications.show({
        message: 'Přidán nový úkol',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      modals.closeAll();
    } catch (err) {
      displayError(err);
    }
  };

  const LabelWithTooltip = ({
    text,
    tooltip,
  }: {
    text: string;
    tooltip: string;
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{text}</span>
      <Tooltip label={tooltip} withArrow withinPortal>
        <IconHelpCircle size={16} color="var(--mantine-color-blue-filled)" />
      </Tooltip>
    </div>
  );

  return (
    <Stack>
      <Text size="lg">Název úkolu</Text>
      <TextInput
        placeholder="název"
        variant="filled"
        radius="md"
        value={form.name}
        onChange={e => {
          updateFormField('name', e.target.value);
        }}
        error={errors.name}
      />
      <Text size="lg">Vlastnosti úkolu</Text>

      <Group justify="space-between" grow align="flex-start">
        <Stack>
          <DatePickerInput
            type="multiple"
            radius="md"
            label="Datum"
            placeholder="datum"
            valueFormat={dateFormatUI}
            rightSection={icon}
            locale="cs"
            maxLevel="month"
            minDate={props.month.startOf('month').toDate()}
            maxDate={props.month.endOf('month').toDate()}
            value={getDates()}
            defaultDate={props.month.startOf('month').toDate()}
            onChange={e => {
              updateFormField(
                'byMonthDay',
                e.map(date => date.getDate())
              );
            }}
            error={errors.date}
          />

          <Checkbox
            checked={checked}
            label="Opakovat měsíčně"
            onChange={event => setChecked(event.currentTarget.checked)}
          />
        </Stack>

        <Select
          variant="filled"
          radius="md"
          label="Preferované pohlaví"
          placeholder="Žena / Muž / Jiné / Nezáleží"
          allowDeselect={true}
          rightSection={icon}
          data={MappingToComboBoxItem(genderMap)}
          value={form.prefSex}
          onChange={e => {
            updateFormField('prefSex', e);
          }}
        />
      </Group>

      <Group justify="space-between" grow align="center">
        <TimeInput
          label={
            <LabelWithTooltip
              text="Začátek nejdříve"
              tooltip="Čas, od kdy nejdříve lze začít pracovat na úkolu"
            />
          }
          value={form.boundStartTime}
          onChange={boundStartTime => {
            updateFormField('boundStartTime', boundStartTime);
          }}
          error={errors.boundStartTime}
        />

        <Textarea
          rows={1}
          label="Poznámka"
          placeholder="poznámka"
          mt="sm"
          variant="filled"
          radius="md"
          styles={{
            input: {
              backgroundColor: '#FFF1D0',
            },
          }}
          value={noteValue}
          onChange={handleChange}
        />
      </Group>
      <Group justify="space-between" grow align="top">
        <TimeInput
          label={
            <LabelWithTooltip
              text="Konec nejpozději"
              tooltip="Čas, od kdy nejdříve lze začít pracovat na úkolu"
            />
          }
          value={form.boundEndTime}
          onChange={boundEndTime => {
            updateFormField('boundEndTime', boundEndTime);
          }}
          error={errors.boundEndTime}
        />
        <MultiSelect
          mt="sm"
          variant="filled"
          radius="md"
          placeholder="přidat tagy"
          label="Tagy"
          rightSection={icon}
          value={selectedValues}
          data={tagOptions}
          onChange={values => {
            handleChangeTags(values);
          }}
        />
      </Group>
      <Group align="flex-start">
        <div>
          <TimeInput
            label="Začátek výkonu"
            style={{
              width: '13vw',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              marginBottom: rem(24), // Match error message height
              position: 'relative',
            }}
            value={form.startTime}
            onChange={startTime => {
              updateFormField('startTime', startTime);
            }}
          />
        </div>
        <div>
          <NumberInput
            label="Trvání"
            placeholder="počet minut"
            mt="sm"
            variant="filled"
            radius="md"
            hideControls
            style={{
              width: '13vw',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              position: 'relative',
            }}
            value={form.duration ? form.duration : ''}
            onChange={e => {
              updateFormField('duration', Number(e));
            }}
            error={errors.duration}
          />
        </div>
      </Group>
      <Divider />
      <Stack>
        <Group justify="flex-end">
          <Button radius="sm" variant="outline" onClick={modals.closeAll}>
            Zrušit
          </Button>
          <Button
            radius="sm"
            onClick={() => {
              handleSubmitButton();
            }}
          >
            Vytvořit úkol
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
};

export const openNewTaskModal = (
  clientPlanId: number | undefined,
  month: Dayjs,
  onClose: () => void // Add onClose callback parameter
) => {
  if (!clientPlanId) {
    notifications.show({
      title: 'Nepodařilo se otevřít okno.',
      message: 'Není vybrán plán nebo služba.',
      color: 'red',
      icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
    });
    return;
  }

  modals.open({
    size: 'xl',
    withCloseButton: false,
    children: <NewTaskModal clientPlanId={clientPlanId} month={month} />,
    onClose, // Pass onClose callback to modals
  });
};
